import React, { useEffect, useMemo } from 'react'
import styles from '@/styles/finisherKill.module.scss'
import type { AltKoVariant, FinisherId } from '@/types/finisher'

// Card dims must match $card-w / $card-h in finisherKill.module.scss.
const CARD_W = 150
const CARD_H = 196
const SHARD_COLS = 3
const SHARD_ROWS = 4

// Alt-variant card animations. Each key matches an alternative finisher
// shader and themes the card's death to it. Omitting koVariant keeps the
// original per-category behavior.
export type { AltKoVariant } from '@/types/finisher'

type KoMode = AltKoVariant | 'bonk' | 'smash' | 'beast'

interface LoserCardKOProps {
  avatarSrc: string
  finisherId: FinisherId
  // Screen-Y ratio (0..1) for the card's vertical center. Default 0.5 (lab).
  anchorYRatio?: number
  onDone?: () => void
  durationMs?: number
  // Override the per-category card animation with an alt-candidate one.
  koVariant?: AltKoVariant
}

// Screen-space DOM clone of the loser's avatar card, destroyed per finisher.
// Decoupled from the 3D character mesh: we just need the avatar texture path.
const LoserCardKO: React.FC<LoserCardKOProps> = ({
  avatarSrc,
  finisherId,
  anchorYRatio = 0.5,
  onDone,
  durationMs = 1600,
  koVariant,
}) => {
  useEffect(() => {
    const id = window.setTimeout(() => onDone?.(), durationMs)
    return () => window.clearTimeout(id)
  }, [durationMs, onDone])

  const mode: KoMode =
    koVariant ?? (finisherId === 'brutality' ? 'smash' : finisherId === 'animality' ? 'beast' : 'bonk')
  const showShards = mode === 'smash' || mode === 'shatter'
  const showHalves = mode === 'beast'
  const showStrips = mode === 'claw'

  // Pre-computed shard cells for Smash!/Shatter! — deterministic fly vectors
  // per index so there's no Math.random churn and reduced-motion stays stable.
  const shards = useMemo(() => {
    if (!showShards) return []
    const cellW = CARD_W / SHARD_COLS
    const cellH = CARD_H / SHARD_ROWS
    const out: React.CSSProperties[] = []
    for (let r = 0; r < SHARD_ROWS; r++) {
      for (let c = 0; c < SHARD_COLS; c++) {
        // Fly outward from card center, magnitude scaled by distance.
        const dx = c - (SHARD_COLS - 1) / 2
        const dy = r - (SHARD_ROWS - 1) / 2
        const tx = dx * 70 + (dx === 0 ? 8 : 0)
        const ty = dy * 70 - 40 // bias upward so it "bursts"
        const rot = (dx + dy) * 24
        out.push({
          left: `${c * cellW}px`,
          top: `${r * cellH}px`,
          width: `${cellW}px`,
          height: `${cellH}px`,
          backgroundImage: `url(${avatarSrc})`,
          backgroundSize: `${CARD_W}px ${CARD_H}px`,
          backgroundPosition: `-${c * cellW}px -${r * cellH}px`,
          ['--tx' as string]: `${tx}px`,
          ['--ty' as string]: `${ty}px`,
          ['--rot' as string]: `${rot}deg`,
        })
      }
    }
    return out
  }, [showShards, avatarSrc])

  const MODE_CLASS: Record<KoMode, string> = {
    bonk: styles.funny,
    smash: styles.brutality,
    beast: styles.animality,
    boing: styles.boing,
    splat: styles.splat,
    dizzy: styles.dizzy,
    slam: styles.slam,
    // Shatter reuses Smash's crack/shard DOM + styling, then overrides the
    // shard flight (farther/faster) via the .shatter modifier.
    shatter: `${styles.brutality} ${styles.shatter}`,
    quake: styles.quake,
    claw: styles.claw,
    howl: styles.howl,
    stampede: styles.stampede,
  }
  const variantClass = MODE_CLASS[mode]

  const top = `calc(${anchorYRatio * 100}vh - ${CARD_H / 2}px)`

  return (
    <div className={styles.wrap} data-testid='loser-card-ko' data-finisher={finisherId}>
      <div className={styles.anchor} style={{ top }}>
        <div className={styles.shaker}>
          <div
            className={`${styles.card} ${variantClass}`}
            // Drive the kill animation length off the finisher duration so the
            // drop spans the whole cinematic (was a fixed 1500ms that finished
            // long before the phase ended, hiding most of the fall).
            style={{ ['--koDur' as string]: `${durationMs}ms` }}
          >
            {/* Halves (Beast!) and strips (Claw!) replace the whole avatar */}
            {!showHalves && !showStrips && (
              <img
                className={styles.img}
                src={avatarSrc}
                alt=''
                draggable={false}
                // Match the warm-up preload + 3D card fetch so this reuses the
                // cached avatar instead of re-downloading mid-animation.
                crossOrigin='anonymous'
              />
            )}

            {showShards && (
              <>
                <div className={styles.crack} />
                <div className={styles.shards}>
                  {shards.map((s, i) => (
                    <div key={i} className={styles.shard} style={s} />
                  ))}
                </div>
              </>
            )}

            {showHalves && (
              <>
                <div
                  className={styles.halfL}
                  style={{ backgroundImage: `url(${avatarSrc})` }}
                />
                <div
                  className={styles.halfR}
                  style={{ backgroundImage: `url(${avatarSrc})` }}
                />
                <div className={styles.slash} />
              </>
            )}

            {showStrips && (
              <>
                <div className={styles.stripA} style={{ backgroundImage: `url(${avatarSrc})` }} />
                <div className={styles.stripB} style={{ backgroundImage: `url(${avatarSrc})` }} />
                <div className={styles.stripC} style={{ backgroundImage: `url(${avatarSrc})` }} />
                {/* one slash sweep per strip, timed to the shader's slashes */}
                <div className={styles.slash} style={{ animationDelay: '350ms' }} />
                <div className={styles.slash} style={{ animationDelay: '850ms' }} />
                <div className={styles.slash} style={{ animationDelay: '1350ms' }} />
              </>
            )}
          </div>
          <div className={styles.stamp}>DEFEATED</div>
        </div>
      </div>
    </div>
  )
}

export default LoserCardKO
